import * as _ from 'underscore';

import { ButtonType, FilteringList, ListItem, LocallyFilteringList } from '@tableau/widgets';
import { Cell, DataGrid } from '@tableau/widgets-datagrid';
import { defaultItemFilter } from '@tableau/widgets/lib/src/widgets/List/ListItemUtils';
import { IAngularEvent } from 'angular';
import * as React from 'react';
import { defaultMemoize } from 'reselect';
import { ServiceLocator } from '../../../app/ServiceLocator';
import { Messages } from '../../../codegen/Localize';
import { ListResult } from '../../../ts/fetchers/ListResult';
import { Filter } from '../../../ts/helpers/Filter';
import { ResourceQuery } from '../../../ts/resources/ResourceQuery';
import { SiteNameSansIds } from '../../../ts/resources/SiteNameSansIds';
import { SiteNameWithIds } from '../../../ts/resources/SiteNameWithIds';
import { ISiteNameSansId } from '../../../ts/ServerApi/types/ISiteNameSansId';

export interface LoginSitePickerState {
  readonly searchText: string;
  readonly items: ListItem[];
  readonly siteNamesRecord: Record<string, ISiteNameSansId>;
}

export interface LoginSitePickerProps {
  readonly handleSelectSiteOption: (siteName: ISiteNameSansId) => void;
}

const PAGESIZE_INITIAL_LOAD = 20;

const createItemElement = (siteName: string) =>
  (<span style={{fontSize: '13px', lineHeight: '37px'}}>
    {siteName}
  </span>);

export class LoginSitePicker extends React.PureComponent<LoginSitePickerProps, LoginSitePickerState> {
  private siteNames: SiteNameSansIds = ServiceLocator.get<SiteNameSansIds>('SiteNamesAcrossAllPods');
  private addAllItemsTimeout: ReturnType<typeof setTimeout> | undefined;
  private siteOptionItems: ListItem[];
  public state = {
    searchText: '',
    items: [] as ListItem[],
    siteNamesRecord: {} as Record<string, ISiteNameSansId>
  };

  public componentDidMount(){
    this.configureAccessibleLoginSitePicker();
  }

  public render(): JSX.Element {
    return (
      <FilteringList
        items={this.state.items}
        onSelect={this.handleSelect}
        searchPlaceholder={Messages.searchForASite()}
        testId="filtering-list-login-site-picker"
        style={{maxHeight: '100%'}}
        onFilterChange={this.onSitePickerFilterChange}
        searchText={this.state.searchText}
        autoFocus={true}
        inputTestId="site-picker-filter"
        listTestId="site-picker-list"
      />
    );
  }

  private handleSelect = (value: string) => {
    this.props.handleSelectSiteOption(this.state.siteNamesRecord[value]);
  }

  private onSitePickerFilterChange = (value: string) => {
    // Clearing out timeout to prevent multiple setState cycles when change filter
    clearTimeout(this.addAllItemsTimeout);
    this.setState({searchText: value});
    const filteredItems = defaultItemFilter(this.siteOptionItems, value);
    this.addItemsToSitePickerFilter(filteredItems);
  }

  private configureAccessibleLoginSitePicker() {
    const query: ResourceQuery<ISiteNameSansId> = this.siteNames.all();
    this.createItems(query);
  }

  /**
   * This method creates the initial site option items for the filtering list, 
   * done in async to allow users to continue to type
   */
  private async createItems(query: ResourceQuery<ISiteNameSansId>) {
    let siteNamesRecord: Record<string, ISiteNameSansId> = {};
    let items: ListItem[] = [];
    query.getCount().then(async (count: number) => {
      query.getSlice(0, count).then((siteNames: ListResult<ISiteNameSansId>) => {
        items = this.createItemsPropForSitePicker(siteNames);
        siteNamesRecord = this.createSiteNamesRecord(siteNames);
        this.siteOptionItems = items;
        this.addItemsToSitePickerFilter(items);
        this.setState({siteNamesRecord});
      });
    });
  }

  private createItemsPropForSitePicker(siteNames: ListResult<ISiteNameSansId>): ListItem[] {
    return siteNames.items.map<ListItem>(siteNameId => (
      {
        content: createItemElement(siteNameId.name),
        value: siteNameId.name,
        testId: 'list-item-' + siteNameId.name
      }));
  }

  private createSiteNamesRecord(siteNames: ListResult<ISiteNameSansId>): Record<string, ISiteNameSansId> {
    return siteNames.items.reduce(function(siteMap: Record<string, ISiteNameSansId>, siteNameId) {
      siteMap[siteNameId.name] = siteNameId;
      return siteMap;
    }, {});
  }

  /**
   * This method is for improved performance when a user is typing, initially load
   * 20 items and after timeout the rest of the items. This is done as loading the 
   * entire list at once takes time to render(O(N)) and prevents user from typing(when large amount of sites)
  **/
  private addItemsToSitePickerFilter(items: ListItem[]) {
    if (items.length > PAGESIZE_INITIAL_LOAD) {
      const initialItems = items.slice(0, PAGESIZE_INITIAL_LOAD-1);
      this.setState({items: initialItems});
      this.addAllItemsTimeout = setTimeout(function() {
        this.setState({items});
      }.bind(this), 1000);
    } else {
      this.setState({items});
    }
  }
}
